import { pure } from '@core/helpers/pure';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
  input,
  OnInit,
  output,
} from '@angular/core';
import { RequestsApiService } from '@core/services/request/requests-api.service';
import { OutkeepPermissionService } from '@core/services/permission/outkeep-permission.service';
import { RequestService } from '@core/services/request/request.service';
import { RequestDevice } from '@core/types/RequestDevice';
import { AsyncPipe } from '@angular/common';
import { TuiDialogService, TuiButton } from '@taiga-ui/core';
import { EMPTY, Observable, Subject, switchMap, tap } from 'rxjs';
import { isBefore } from 'date-fns';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { PolymorpheusComponent } from '@taiga-ui/polymorpheus';
import { Nullable } from '@core/helpers/nullable';
import { Position } from '@core/components/position-definer/position-definer-form/types';
import { RequestDevicesService } from '@core/services/request/request-devices.service';
import { PositionDefinerDialogComponent } from '@core/components/position-definer/position-definer-dialog/position-definer-dialog.component';
import { WarehouseService } from '@core/services/warehouse/warehouse.service';
import { TranslatePipe } from '@ngx-translate/core';

@Component({
  selector: 'x-device-location-updater',
  imports: [AsyncPipe, TuiButton, TranslatePipe],
  templateUrl: './device-location-updater.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DeviceLocationUpdaterComponent implements OnInit {
  private readonly requestService = inject(RequestService);
  private readonly requestApiService = inject(RequestsApiService);
  private readonly warehouseService = inject(WarehouseService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly dialogService$ = inject(TuiDialogService);
  private readonly requestDevicesService = inject(RequestDevicesService);
  readonly permissionService = inject(OutkeepPermissionService);
  readonly $request = this.requestService.$request;
  readonly $device = input.required<RequestDevice>({ alias: 'device' });
  readonly updateLocation$ = new Subject<void>();

  deviceLocationUpdated = output<void>();

  ngOnInit(): void {
    this.updateLocation$
      .pipe(
        switchMap(this.getPosition$.bind(this)),
        switchMap(this.performLocationUpdate$.bind(this)),
        // switchMap(() =>
        //   this.requestDevicesService.checkWarehouseDeviceSoldAttempt$(
        //     this.device.name,
        //   ),
        // ),
        switchMap(() =>
          this.warehouseService.unSellDevice$(
            this.$request(),
            this.device.name,
          ),
        ),
        tap(() => {
          this.deviceLocationUpdated.emit();
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  @pure
  get device() {
    return this.$device();
  }

  @pure
  get lastDeviceDatetime(): Date | null {
    return this.requestDevicesService.getLastDeviceDatetimeOf(this.device);
  }

  @pure
  get lastLocationDatetime(): Date | null {
    return this.requestDevicesService.getLastLocationDatetimeOf(this.device);
  }

  @pure
  get daysBetweenLocationAndLastDate(): number | null {
    return this.requestDevicesService.getDaysBetweenLocationAndLastDateOf(
      this.device,
    );
  }

  @pure
  get isLocationDeprecated(): boolean {
    const location = this.device.status?.location;

    if (!location) {
      return false;
    }

    const lastDeviceDatetime = this.lastDeviceDatetime;
    const lastLocationDatetime = this.lastLocationDatetime;

    if (lastLocationDatetime && lastDeviceDatetime) {
      return isBefore(
        new Date(lastLocationDatetime),
        new Date(lastDeviceDatetime),
      );
    }

    const daysBetweenLocationAndLastDate = this.daysBetweenLocationAndLastDate;

    if (daysBetweenLocationAndLastDate !== null) {
      return daysBetweenLocationAndLastDate > 0;
    }

    return false;
  }

  private getPosition$(): Observable<Nullable<Position>> {
    return this.dialogService$.open(
      new PolymorpheusComponent(PositionDefinerDialogComponent),
    );
  }

  private performLocationUpdate$(
    position: Nullable<Position>,
  ): Observable<void> {
    if (!position) {
      return EMPTY;
    }

    return this.requestApiService.updateLocationForTracker$(
      this.device.deviceHash,
      position.coords,
    );
  }
}
